import React, { useState, useEffect, useCallback } from "react";
import { Spin, Button, Row, Col, Card, Select, Popover, Tag } from "antd";
import { LoadingOutlined, FilterOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import SalesStatCard from "./SalesStatCard";
import { roundDisplayPrice } from "../../../utils/priceHelpers";
import {
  _get_support_dashboard_sales,
  _get_support_sales_breakdown_weekly,
  _get_support_sales_breakdown_daily,
  _get_support_staffs,
} from "../../../common/axios_services";
import "../styles/mobile-datepicker.css";

const AdminSalesContent = ({ useOwnLayout = false }) => {
  const userProfile = useSelector((state) => state.userProfile?.user);
  const userRole = userProfile?.role || "";
  const canFilter =
    userRole && ["ADMIN", "SUPPORT_EXECUTIVE"].includes(userRole.toUpperCase());

  const [dashboardData, setDashboardData] = useState({
    summary: {},
    breakdown: {},
  });
  const [weeklyData, setWeeklyData] = useState({
    weeks: [],
    month: "",
    total: 0,
  });
  const [dailyData, setDailyData] = useState({ days: [], week: "", total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState("today");
  const [selectedStaffs, setSelectedStaffs] = useState([]);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [supportManagers, setSupportManagers] = useState([]);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const getDateRange = useCallback((period) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const todayStr = `${year}-${month}-${day}`;

    if (period === "today") {
      return { dateFrom: todayStr, dateTo: todayStr };
    }

    if (period === "thisWeek") {
      const firstDayOfWeek = new Date(today);
      firstDayOfWeek.setDate(today.getDate() - today.getDay());
      const fyear = firstDayOfWeek.getFullYear();
      const fmonth = String(firstDayOfWeek.getMonth() + 1).padStart(2, "0");
      const fday = String(firstDayOfWeek.getDate()).padStart(2, "0");
      const startStr = `${fyear}-${fmonth}-${fday}`;
      return { dateFrom: startStr, dateTo: todayStr };
    }

    if (period === "thisMonth") {
      const firstDayOfMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );
      const fyear = firstDayOfMonth.getFullYear();
      const fmonth = String(firstDayOfMonth.getMonth() + 1).padStart(2, "0");
      const fday = String(firstDayOfMonth.getDate()).padStart(2, "0");
      const startStr = `${fyear}-${fmonth}-${fday}`;
      return { dateFrom: startStr, dateTo: todayStr };
    }

    return { dateFrom: "2020-01-01", dateTo: todayStr };
  }, []);

  const fetchAllData = useCallback(async (df, dt, staffIds = []) => {
    try {
      setLoading(true);
      setError(null);
      const smId = staffIds.length > 0 ? staffIds[0] : null;
      const soId = staffIds.length > 1 ? staffIds[1] : null;
      const [dashRes, weekRes, dayRes] = await Promise.all([
        _get_support_dashboard_sales(df, dt, smId, soId),
        _get_support_sales_breakdown_weekly(df, dt, smId, soId),
        _get_support_sales_breakdown_daily(df, dt, smId, soId),
      ]);
      setDashboardData(dashRes?.data?.data || { summary: {}, breakdown: {} });
      setWeeklyData(weekRes?.data?.data || { weeks: [], month: "", total: 0 });
      setDailyData(dayRes?.data?.data || { days: [], week: "", total: 0 });
    } catch (err) {
      setError(err.message || "Failed to load sales data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const dates = getDateRange(timePeriod);
    fetchAllData(dates.dateFrom, dates.dateTo, selectedStaffs);
  }, [timePeriod, selectedStaffs, getDateRange, fetchAllData]);

  const fetchSupportManagers = async () => {
    try {
      const response = await _get_support_staffs({
        limit: 100,
      });
      const staffData =
        response?.data?.data?.data || response?.data?.data || [];
      setSupportManagers(Array.isArray(staffData) ? staffData : []);
    } catch (error) {
      console.error("Error fetching support staffs:", error);
      setSupportManagers([]);
    }
  };

  const filterableStaffs = supportManagers.filter(
    (staff) => staff.role !== "SUPPORT_EXECUTIVE",
  );

  const getRoleColor = (role) => {
    switch (role) {
      case "SUPPORT_MANAGER":
        return "blue";
      case "SUPPORT_OFFICER":
        return "green";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "SUPPORT_MANAGER":
        return "SM";
      case "SUPPORT_OFFICER":
        return "SO";
      default:
        return role;
    }
  };

  useEffect(() => {
    if (canFilter) {
      fetchSupportManagers();
    }
  }, [canFilter]);

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className={useOwnLayout ? "pb-40" : ""}>
        <div className="main_container_title_div mb-5 mt-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Sales Dashboard
          </h1>
          <p className="text-gray-600 sm:text-base">
            Monitor sales performance across the platform
          </p>
        </div>

        <div className="mb-6 flex gap-2">
          <Button
            type={timePeriod === "today" ? "primary" : "default"}
            onClick={() => setTimePeriod("today")}
          >
            Today
          </Button>
          <Button
            type={timePeriod === "thisWeek" ? "primary" : "default"}
            onClick={() => setTimePeriod("thisWeek")}
          >
            This Week
          </Button>
          <Button
            type={timePeriod === "thisMonth" ? "primary" : "default"}
            onClick={() => setTimePeriod("thisMonth")}
          >
            This Month
          </Button>
          <Button
            type={timePeriod === "allTime" ? "primary" : "default"}
            onClick={() => setTimePeriod("allTime")}
          >
            All Time
          </Button>
          {canFilter && (
            <Popover
              content={
                <div style={{ width: "260px" }}>
                  <label
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Support Staff
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="Select staff members"
                    value={selectedStaffs}
                    onChange={setSelectedStaffs}
                    maxTagCount="responsive"
                    style={{ width: "100%" }}
                    options={filterableStaffs.map((staff) => ({
                      value: staff.id,
                      label: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>
                            {staff.firstName} {staff.lastName}
                          </span>
                          <Tag
                            color={getRoleColor(staff.role)}
                            style={{ margin: 0 }}
                          >
                            {getRoleLabel(staff.role)}
                          </Tag>
                        </div>
                      ),
                    }))}
                  />
                </div>
              }
              trigger="click"
              open={filterPopoverVisible}
              onOpenChange={setFilterPopoverVisible}
              placement="bottomLeft"
            >
              <Button
                icon={<FilterOutlined />}
                type={selectedStaffs.length > 0 ? "primary" : "default"}
              />
            </Popover>
          )}
        </div>

        <div className="mb-6 bg-white p-5 border-gray-200">
          <h3 className="font-gilroyBold text-2xl mb-5">Sales Summary</h3>
          <Row gutter={[20, 20]}>
            <Col xs={12} sm={8} md={6}>
              <SalesStatCard
                label="Sales Today"
                value={dashboardData?.summary?.salesTodayNGN || 0}
                isCurrency
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <SalesStatCard
                label="Sales This Week"
                value={dashboardData?.summary?.salesThisWeekNGN || 0}
                isCurrency
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <SalesStatCard
                label="Sales This Month"
                value={dashboardData?.summary?.salesThisMonthNGN || 0}
                isCurrency
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <SalesStatCard
                label="Approved Orders"
                value={dashboardData?.summary?.approved || 0}
              />
            </Col>
          </Row>
        </div>

        {dashboardData?.breakdown?.byZone &&
          dashboardData.breakdown.byZone.length > 0 && (
            <div className="mb-6 bg-white p-5 border-gray-200">
              <h3 className="font-gilroyBold text-2xl mb-5">Sales by Zone</h3>
              {dashboardData.breakdown.byZone.map((zone) => (
                <div key={zone.zoneId} className="mb-8">
                  <h4 className="font-semibold text-gray-900 text-xl mb-4">
                    {zone.zoneName}
                  </h4>
                  <Row gutter={[20, 20]}>
                    <Col xs={12} sm={8} md={6}>
                      <SalesStatCard
                        label="Sales This Month"
                        value={zone.stats?.salesThisMonthNGN || 0}
                        isCurrency
                      />
                    </Col>
                    {zone.supportOfficers &&
                      zone.supportOfficers.slice(0, 3).map((officer) => (
                        <Col key={officer.officerId} xs={12} sm={8} md={6}>
                          <SalesStatCard
                            label={officer.officerName}
                            value={officer.stats?.salesThisMonthNGN || 0}
                            isCurrency
                          />
                        </Col>
                      ))}
                  </Row>
                </div>
              ))}
            </div>
          )}

        {Array.isArray(weeklyData?.weeks) && weeklyData.weeks.length > 0 && (
          <div className="mb-6 bg-white p-5 border-gray-200">
            <h3 className="font-gilroyBold text-2xl mb-5">
              Monthly Breakdown ({weeklyData?.month || "-"})
            </h3>
            <p className="text-green-600 text-2xl font-gilroyBold mb-4">
              Total: ₦{roundDisplayPrice(weeklyData?.total)}
            </p>
            {weeklyData.weeks.map((week) => (
              <div key={week.weekNumber} className="mb-8">
                <h4 className="font-semibold text-gray-900 text-xl mb-4">
                  Week {week.weekNumber} ({week.startDate} - {week.endDate})
                </h4>
                <Row gutter={[20, 20]}>
                  <Col xs={12} sm={8} md={6}>
                    <SalesStatCard
                      label="Sales"
                      value={week.salesNGN || 0}
                      isCurrency
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <SalesStatCard
                      label="Orders"
                      value={week.orderCount || 0}
                    />
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}

        {Array.isArray(dailyData?.days) && dailyData.days.length > 0 && (
          <div className="mb-6 bg-white p-5 border-gray-200">
            <h3 className="font-gilroyBold text-2xl mb-5">
              Daily Breakdown ({dailyData?.week || "-"})
            </h3>
            <p className="text-green-600 text-2xl font-gilroyBold mb-4">
              Total: ₦{roundDisplayPrice(dailyData?.total)}
            </p>
            {dailyData.days.map((day) => (
              <div key={day.date} className="mb-8">
                <h4 className="font-semibold text-gray-900 text-xl mb-4">
                  {day.dayName} ({day.date})
                </h4>
                <Row gutter={[20, 20]}>
                  <Col xs={12} sm={8} md={6}>
                    <SalesStatCard
                      label="Sales"
                      value={day.salesNGN || 0}
                      isCurrency
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <SalesStatCard label="Orders" value={day.orderCount || 0} />
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}

        {error && (
          <Card className="mb-6 bg-red-50 border-red-200">
            <p className="text-red-600">{error}</p>
          </Card>
        )}
      </div>
    </Spin>
  );
};

export default AdminSalesContent;
